/**
 * Margin Report Configuration
 *
 * Constants and configuration for derived rows and explanations.
 * @see US-25d.12 - Composant MarginReportTable
 */

/**
 * Configuration for derived rows - defines how to calculate values from other rows
 */
export interface DerivedRowConfig {
  /** Formula type */
  type: 'subtract' | 'add' | 'subtract_multiple' | 'signed';
  /** Source row IDs for the calculation */
  sources: string[];
  /** Signs for each source (only for type 'signed'): +1 or -1 */
  signs?: (1 | -1)[];
  /** Which value fields are derived (theo, reel, or both) */
  fields: ('theo' | 'reel')[];
}

/**
 * Map of derived row IDs to their calculation config
 */
export const DERIVED_ROWS: Record<string, DerivedRowConfig> = {
  'fc-brut': {
    // FC Brut Reel = SI + Entrees - Sorties - SF
    type: 'signed',
    sources: ['stock-initial', 'stock-entrees', 'stock-sorties', 'stock-final'],
    signs: [1, 1, -1, -1],
    fields: ['reel'],
  },
  'marge-brute': {
    type: 'subtract',
    sources: ['ca-net', 'fc-brut'], // ca-net - fc-brut
    fields: ['theo', 'reel'],
  },
  'deductions-pertes': {
    // Pertes connues = Brut + Fini + Exceptionnel
    type: 'add',
    sources: ['pertes-brut', 'pertes-fini', 'pertes-exceptionnel'],
    fields: ['theo', 'reel'],
  },
  'fc-net': {
    // FC Net = FC Brut - Promos - Repas - Pertes
    type: 'subtract_multiple',
    sources: ['fc-brut', 'deductions-promos', 'deductions-repas', 'deductions-pertes'],
    fields: ['theo', 'reel'],
  },
  // ecart-total uses the API value directly (totalVariance = FC Net Reel - FC Net Theo)
  // bulk + nonBulk now correctly sum to this value since losses are subtracted per product
};

/**
 * Explanations for source (non-derived) rows
 * Key: row ID, Value: { theo?: string, reel?: string }
 */
export const ROW_EXPLANATIONS: Record<string, { theo?: string; reel?: string }> = {
  'ca-brut': {
    theo: 'Somme des ventes POS sur la periode (prix plein tarif reconstitue)',
    reel: 'Somme des ventes POS sur la periode (prix plein tarif reconstitue)',
  },
  'ca-net': {
    theo: 'Ventes Brutes - Valeur remisee = CA reel encaisse',
    reel: 'Ventes Brutes - Valeur remisee = CA reel encaisse',
  },
  'stock-initial': {
    reel: 'Valeur stock au debut de periode (inventaire + mouvements)',
  },
  'stock-initial-food': {
    reel: 'Stock initial produits FOOD',
  },
  'stock-initial-food-ind': {
    reel: 'Stock initial produits FOOD IND',
  },
  'stock-initial-non-food': {
    reel: 'Stock initial produits NON FOOD',
  },
  'stock-initial-non-food-ind': {
    reel: 'Stock initial produits NON FOOD IND',
  },
  'stock-entrees': {
    reel: 'Somme des receptions valorisees sur la periode',
  },
  'stock-sorties': {
    reel: 'Somme des transferts sortants sur la periode',
  },
  'stock-final': {
    reel: 'Valeur stock en fin de periode (inventaire)',
  },
  'fc-brut': {
    theo: 'Consommation POS + Pertes declarees',
  },
  'deductions-promos': {
    theo: 'Cout de revient des produits offerts',
    reel: 'Cout de revient des produits offerts',
  },
  'deductions-repas': {
    theo: 'Cout de revient des repas employes',
    reel: 'Cout de revient des repas employes',
  },
  'deductions-pertes': {
    theo: 'Somme des pertes declarees valorisees',
    reel: 'Somme des pertes declarees valorisees',
  },
  'pertes-brut': {
    theo: 'Pertes declarees : matieres premieres (brut)',
    reel: 'Pertes declarees : matieres premieres (brut)',
  },
  'pertes-fini': {
    theo: 'Pertes declarees : produits finis',
    reel: 'Pertes declarees : produits finis',
  },
  'pertes-exceptionnel': {
    theo: 'Pertes declarees : exceptionnelles',
    reel: 'Pertes declarees : exceptionnelles',
  },
  'ecart-bulk': {
    reel: 'Ecart conso reelle vs theorique sur produits Bulk',
  },
  'ecart-nonbulk': {
    reel: 'Ecart conso reelle vs theorique sur produits Non-Bulk (demarque)',
  },
  'hors-fc-maintenance': {
    reel: 'Consommation produits MAINTENANCE sur la periode',
  },
  'fc-brut-bulk': {
    reel: 'Part du Food Cost Brut portee par les produits Bulk : Stock Initial + Entrees - Sorties - Stock Final',
  },
};
